/* eslint-disable consistent-return */
import { createApi } from '@reduxjs/toolkit/query/react';
import { getCookie, setCookie } from 'typescript-cookie';
import { axiosBaseQuery } from './AxiosBaseQuery';
import { API_SERVER_URL, PRODUCT_GALLERY_API_CONTEXT } from '@/constants/Config';
import { removeDuplicates } from '@/utilities/CommonUtils';
import type { ProductsResponse } from '@/utilities/ProductGalleryUtils';

interface IErrRes {
  message: string;
  status: string;
}

/** Defining a service using a base URL and expected endpoints  */
export const productsApi = createApi({
  reducerPath: 'productsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: API_SERVER_URL,
  }),
  endpoints: (build) => ({
    getProducts: build.query({
      query: ({ limit, page, params }) => ({
        url: `api/products?limit=${limit}&offset=${page}&${params}&context=${PRODUCT_GALLERY_API_CONTEXT}`,
        method: 'get',
      }),
      transformErrorResponse: (response: IErrRes) => ({
        message: response.message,
        status: 'error',
      }),
      transformResponse: (response: ProductsResponse) => {
        const transformedProducts = response.page.map((product) => {
          const newVariant = {
            code: product.color.code,
            name: product.color.name,
            imgUrl: product.imgUrl,
            location: 'Front',
          };

          return {
            ...product,
            variants: [newVariant, ...(product.variants || [])],
          };
        });

        return {
          products: transformedProducts || [],
          count: response.count,
          currentArgs: response.currentArgs,
        };
      },
      /** Only have one cache entry because the arg always maps to one string   */
      serializeQueryArgs: ({ endpointName }) => endpointName,
      /** Always merge incoming data to the cache entry */
      merge: (currentCache, newItems, { arg }) => {
        if (JSON.stringify(getCookie('prevParams')) !== JSON.stringify(arg.params)) {
          return newItems;
        }

        currentCache.products.push(...newItems.products);
        currentCache.products = removeDuplicates(currentCache?.products, 'id');
        setCookie('prevParams', arg.params);
      },
      /** Refetch when the page arg changes */
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
    }),
  }),
});

/** Export hooks for usage in functional components, which are auto-generated based on the defined endpoints */
export const { useGetProductsQuery } = productsApi;
